import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { startOfMonth, endOfMonth, subMonths, subDays, format, differenceInDays } from "date-fns";

// =====================================================
// TYPES
// =====================================================

export interface FinancialKPIs {
  workOrders: {
    total: number;
    completed: number;
    inProgress: number;
    pending: number;
    completionRate: number;
    onTimeRate: number;
  };
  currentMonth: {
    total: number;
    completed: number;
  };
  previousMonth: {
    total: number;
    completed: number;
  };
}

export interface ProductionData {
  totalUnitsProduced: number;
  previousMonthUnits: number;
  averageCycleTimeMinutes: number;
  weeklyProduction: WeeklyProductionItem[];
}

export interface WeeklyProductionItem {
  day: string;
  produced: number;
  target: number;
  defects: number;
}

export interface QualityMetrics {
  totalInspections: number;
  passed: number;
  failed: number;
  passRate: number;
  totalDefects: number;
}

export interface CustomerData {
  id: string;
  name: string;
  orderCount: number;
  completedCount: number;
  color: string;
}

export interface SmartAlert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  prediction: string;
  action: string;
  actionType: 'navigate' | 'create_order' | 'schedule_maintenance' | 'view_details';
  time: string;
  workOrderCode?: string;
  materialName?: string;
  machineCode?: string;
  moldCode?: string;
  navigateTo?: string;
}

export interface MonthlyTrendItem {
  month: string;
  orders: number;
  completed: number;
  completionRate: number;
}

export interface InventoryValueData {
  materialsValue: number;
  rawMaterialsValue: number;
  totalInventoryValue: number;
}

export interface ExecutiveKPIs {
  financial: FinancialKPIs;
  production: ProductionData;
  quality: QualityMetrics;
  inventory: InventoryValueData;
  customers: CustomerData[];
  alerts: SmartAlert[];
  monthlyTrend: MonthlyTrendItem[];
  lastUpdated: Date;
}

// =====================================================
// HELPER FUNCTIONS
// =====================================================

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

function getDateRanges() {
  const now = new Date();
  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);
  const previousMonthStart = startOfMonth(subMonths(now, 1));
  const previousMonthEnd = endOfMonth(subMonths(now, 1));

  return {
    currentMonthStart: format(currentMonthStart, 'yyyy-MM-dd'),
    currentMonthEnd: format(currentMonthEnd, 'yyyy-MM-dd'),
    previousMonthStart: format(previousMonthStart, 'yyyy-MM-dd'),
    previousMonthEnd: format(previousMonthEnd, 'yyyy-MM-dd'),
    today: format(now, 'yyyy-MM-dd'),
  };
}

function timeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} min`;
  if (diffHours < 24) return `${diffHours}h`;
  return `${diffDays}d`;
}

// =====================================================
// MAIN HOOK
// =====================================================

export function useExecutiveKPIs(timeRange: 'week' | 'month' | 'quarter' | 'year' = 'month') {
  return useQuery({
    queryKey: ['executive_kpis', timeRange],
    queryFn: async (): Promise<ExecutiveKPIs> => {
      const dates = getDateRanges();

      // Parallel queries for better performance
      const [
        workOrdersResult,
        currentMonthWOResult,
        previousMonthWOResult,
        productionBatchesResult,
        qualityResult,
        customersResult,
        lowStockResult,
        materialLotsResult,
        rawMaterialsValueResult,
        moldMaintenanceResult,
        machineMaintenanceResult,
        overdueWOResult,
        monthlyTrendResult,
      ] = await Promise.all([
        // All work orders
        api.from('work_orders').select('id, status, due_date, completed_at, quantity', { count: 'exact' }).eq('is_active', true),

        // Current month work orders
        api.from('work_orders').select('id, status, completed_at', { count: 'exact' })
          .gte('created_at', dates.currentMonthStart)
          .lte('created_at', dates.currentMonthEnd + 'T23:59:59'),

        // Previous month work orders
        api.from('work_orders').select('id, status, completed_at', { count: 'exact' })
          .gte('created_at', dates.previousMonthStart)
          .lte('created_at', dates.previousMonthEnd + 'T23:59:59'),

        // Production batches for units
        api.from('production_batches').select('id, quantity, status, created_at'),

        // Quality inspections
        api.from('quality_inspections').select('id, status, defects_count, created_at'),

        // Customers with their work orders count
        api.from('customers').select('id, name').eq('is_active', true).limit(10),

        // Materials with cost data for value calculation
        api.from('material_inventory').select('id, name, code, current_stock, min_stock, square_meters, cost_per_unit')
          .eq('is_active', true),

        // Material lots (for accurate square_meters sum)
        api.from('material_lots').select('material_id, square_meters')
          .eq('is_active', true),

        // Raw materials (for inventory value calculation) - use quantity × cost
        api.from('raw_materials').select('id, name, quantity, min_stock, cost_per_unit')
          .eq('is_active', true),

        // Molds needing maintenance
        api.from('molds').select('id, name, code, current_uses, max_uses, next_maintenance_date')
          .eq('is_active', true),

        // Machines needing maintenance (based on last_maintenance date)
        api.from('machines').select('id, name, code, last_maintenance')
          .eq('is_active', true),

        // Overdue work orders
        api.from('work_orders').select('id, order_number, product_name, due_date, status')
          .eq('is_active', true)
          .neq('status', 'completed')
          .neq('status', 'cancelled')
          .lt('due_date', dates.today),

        // Monthly trend (last 6 months)
        api.from('work_orders').select('id, status, created_at, completed_at')
          .gte('created_at', format(subMonths(new Date(), 6), 'yyyy-MM-dd'))
          .eq('is_active', true),
      ]);

      // Process work orders
      const workOrders = workOrdersResult.data || [];
      const completed = workOrders.filter((wo: any) => wo.status === 'completed').length;
      const inProgress = workOrders.filter((wo: any) => wo.status === 'in_progress').length;
      const pending = workOrders.filter((wo: any) => wo.status === 'pending').length;

      // Calculate on-time rate
      const completedWithDueDate = workOrders.filter((wo: any) =>
        wo.status === 'completed' && wo.due_date && wo.completed_at
      );
      const onTime = completedWithDueDate.filter((wo: any) =>
        new Date(wo.completed_at) <= new Date(wo.due_date + 'T23:59:59')
      ).length;
      const onTimeRate = completedWithDueDate.length > 0
        ? (onTime / completedWithDueDate.length) * 100
        : 100;

      // Current vs previous month
      const currentMonthWO = currentMonthWOResult.data || [];
      const previousMonthWO = previousMonthWOResult.data || [];
      const currentCompleted = currentMonthWO.filter((wo: any) => wo.status === 'completed').length;
      const previousCompleted = previousMonthWO.filter((wo: any) => wo.status === 'completed').length;

      // Production
      const batches = productionBatchesResult.data || [];
      const totalUnitsProduced = batches
        .filter((b: any) => b.status === 'completed')
        .reduce((sum: number, b: any) => sum + (b.quantity || 0), 0);

      // Quality
      const inspections = qualityResult.data || [];
      const passedInspections = inspections.filter((i: any) => i.status === 'passed').length;
      const failedInspections = inspections.filter((i: any) => i.status === 'failed').length;
      const totalDefects = inspections.reduce((sum: number, i: any) => sum + (i.defects_count || 0), 0);
      const passRate = inspections.length > 0
        ? (passedInspections / inspections.length) * 100
        : 100;

      // Customer data - get order counts per customer
      const customers = customersResult.data || [];
      const customerWorkOrders = await Promise.all(
        customers.slice(0, 6).map(async (customer: any) => {
          const { data: orders } = await api.from('work_orders')
            .select('id, status', { count: 'exact' })
            .eq('customer_id', customer.id)
            .eq('is_active', true);

          const orderList = orders || [];
          return {
            id: customer.id,
            name: customer.name,
            orderCount: orderList.length,
            completedCount: orderList.filter((o: any) => o.status === 'completed').length,
          };
        })
      );

      const customerData: CustomerData[] = customerWorkOrders
        .filter(c => c.orderCount > 0)
        .sort((a, b) => b.orderCount - a.orderCount)
        .slice(0, 6)
        .map((c, i) => ({
          ...c,
          color: COLORS[i % COLORS.length],
        }));

      // Generate alerts
      const alerts: SmartAlert[] = [];

      // Alert: Overdue work orders
      const overdueWOs = overdueWOResult.data || [];
      overdueWOs.forEach((wo: any, index: number) => {
        if (index < 2) { // Limit to 2 overdue alerts
          const daysOverdue = differenceInDays(new Date(), new Date(wo.due_date));
          alerts.push({
            id: `overdue-${wo.id}`,
            type: 'critical',
            title: 'Orden retrasada',
            message: `${wo.order_number} (${wo.product_name || 'Sin producto'}) lleva ${daysOverdue} días de retraso`,
            prediction: `${Math.min(95, 70 + daysOverdue * 5)}% probabilidad de impacto`,
            action: 'Ver orden',
            actionType: 'navigate',
            time: timeAgo(new Date(wo.due_date)),
            workOrderCode: wo.order_number,
            navigateTo: '/work-orders',
          });
        }
      });

      // Alert: Low stock
      const lowStockItems = (lowStockResult.data || []).filter((m: any) =>
        m.current_stock !== null && m.min_stock !== null && m.current_stock <= m.min_stock
      );
      lowStockItems.forEach((item: any, index: number) => {
        if (index < 2) { // Limit to 2 prepreg stock alerts
          alerts.push({
            id: `lowstock-prepreg-${item.id}`,
            type: 'warning',
            title: 'Preimpregnado bajo',
            message: `${item.name} (${item.current_stock} uds)`,
            prediction: `Mínimo: ${item.min_stock} unidades`,
            action: 'Generar pedido',
            actionType: 'create_order',
            time: '2h',
            materialName: item.name,
          });
        }
      });

      // Alert: Low stock raw materials
      const lowStockRawMaterials = (rawMaterialsValueResult.data || []).filter((m: any) =>
        m.quantity !== null && m.min_stock !== null && m.min_stock > 0 && m.quantity <= m.min_stock
      );
      lowStockRawMaterials.forEach((item: any, index: number) => {
        if (index < 2) { // Limit to 2 raw material stock alerts
          alerts.push({
            id: `lowstock-raw-${item.id}`,
            type: 'warning',
            title: 'Materia prima baja',
            message: `${item.name} (${item.quantity} uds)`,
            prediction: `Mínimo: ${item.min_stock} unidades`,
            action: 'Generar pedido',
            actionType: 'create_order',
            time: '2h',
            materialName: item.name,
          });
        }
      });

      // Alert: Mold maintenance
      const moldsNeedingMaint = (moldMaintenanceResult.data || []).filter((m: any) => {
        if (m.max_uses && m.current_uses >= m.max_uses * 0.9) return true;
        if (m.next_maintenance_date && new Date(m.next_maintenance_date) <= new Date()) return true;
        return false;
      });
      moldsNeedingMaint.forEach((mold: any, index: number) => {
        if (index < 1) {
          const isOverdue = mold.next_maintenance_date && new Date(mold.next_maintenance_date) <= new Date();
          alerts.push({
            id: `mold-maint-${mold.id}`,
            type: isOverdue ? 'critical' : 'info',
            title: 'Mantenimiento de molde',
            message: `${mold.name} (${mold.code}) requiere mantenimiento`,
            prediction: mold.max_uses
              ? `Usos: ${mold.current_uses}/${mold.max_uses}`
              : 'Mantenimiento programado',
            action: 'Programar',
            actionType: 'schedule_maintenance',
            time: '1d',
            moldCode: mold.code,
          });
        }
      });

      // Alert: Success if good completion rate
      if (onTimeRate >= 90 && completed > 0) {
        alerts.push({
          id: 'success-ontime',
          type: 'success',
          title: 'Excelente cumplimiento',
          message: `${onTimeRate.toFixed(0)}% de órdenes entregadas a tiempo`,
          prediction: 'Tendencia positiva mantenida',
          action: 'Ver detalles',
          actionType: 'view_details',
          time: '1d',
          navigateTo: '/reports',
        });
      }

      // Calculate inventory values using material_lots for accurate square_meters
      const materialLots = materialLotsResult.data || [];
      const materials = lowStockResult.data || [];

      // Sum square_meters from lots grouped by material_id
      const lotsByMaterial: Record<string, number> = {};
      for (const lot of materialLots) {
        const mid = lot.material_id;
        lotsByMaterial[mid] = (lotsByMaterial[mid] || 0) + (lot.square_meters || 0);
      }

      // Calculate materials value: lots_square_meters * cost_per_unit
      const materialsValue = materials.reduce((sum: number, m: any) => {
        const sqMeters = lotsByMaterial[m.id] || m.square_meters || 0;
        return sum + (sqMeters * (m.cost_per_unit || 0));
      }, 0);

      const rawMaterialsValue = (rawMaterialsValueResult.data || []).reduce(
        (sum: number, m: any) => sum + ((m.quantity || 0) * (m.cost_per_unit || 0)),
        0
      );
      const totalInventoryValue = materialsValue + rawMaterialsValue;

      // Monthly trend (last 6 months)
      const monthlyOrders = monthlyTrendResult.data || [];
      const monthlyTrend: MonthlyTrendItem[] = [];

      for (let i = 5; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        const monthName = format(monthDate, 'MMM');

        const monthOrders = monthlyOrders.filter((wo: any) => {
          const created = new Date(wo.created_at);
          return created >= monthStart && created <= monthEnd;
        });

        const monthCompleted = monthOrders.filter((wo: any) => wo.status === 'completed').length;

        monthlyTrend.push({
          month: monthName,
          orders: monthOrders.length,
          completed: monthCompleted,
          completionRate: monthOrders.length > 0
            ? (monthCompleted / monthOrders.length) * 100
            : 0,
        });
      }

      // Weekly production (last 7 days)
      const weeklyProduction: WeeklyProductionItem[] = [];
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

      for (let i = 6; i >= 0; i--) {
        const day = new Date();
        day.setDate(day.getDate() - i);
        const dayStart = format(day, 'yyyy-MM-dd');

        const dayBatches = batches.filter((b: any) =>
          b.created_at && b.created_at.startsWith(dayStart)
        );

        const produced = dayBatches
          .filter((b: any) => b.status === 'completed')
          .reduce((sum: number, b: any) => sum + (b.quantity || 0), 0);

        weeklyProduction.push({
          day: dayNames[day.getDay()],
          produced,
          target: 25, // Default target - could be configurable
          defects: 0, // Would need defects data per day
        });
      }

      return {
        financial: {
          workOrders: {
            total: workOrders.length,
            completed,
            inProgress,
            pending,
            completionRate: workOrders.length > 0 ? (completed / workOrders.length) * 100 : 0,
            onTimeRate,
          },
          currentMonth: {
            total: currentMonthWO.length,
            completed: currentCompleted,
          },
          previousMonth: {
            total: previousMonthWO.length,
            completed: previousCompleted,
          },
        },
        production: {
          totalUnitsProduced,
          previousMonthUnits: 0, // Would need previous month batches
          averageCycleTimeMinutes: 0, // Would calculate from process_records
          weeklyProduction,
        },
        quality: {
          totalInspections: inspections.length,
          passed: passedInspections,
          failed: failedInspections,
          passRate,
          totalDefects,
        },
        inventory: {
          materialsValue,
          rawMaterialsValue,
          totalInventoryValue,
        },
        customers: customerData,
        alerts: alerts.slice(0, 4), // Limit to 4 alerts
        monthlyTrend,
        lastUpdated: new Date(),
      };
    },
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // 5 minutes auto-refresh
  });
}

// =====================================================
// INDIVIDUAL HOOKS FOR SPECIFIC SECTIONS
// =====================================================

export function useWorkOrderStats() {
  return useQuery({
    queryKey: ['work_order_stats'],
    queryFn: async () => {
      const { data, error } = await api.from('work_orders')
        .select('id, status, due_date, completed_at')
        .eq('is_active', true);

      if (error) throw error;

      const orders = data || [];
      const total = orders.length;
      const completed = orders.filter((o: any) => o.status === 'completed').length;
      const inProgress = orders.filter((o: any) => o.status === 'in_progress').length;
      const pending = orders.filter((o: any) => o.status === 'pending').length;
      const overdue = orders.filter((o: any) =>
        o.status !== 'completed' &&
        o.status !== 'cancelled' &&
        o.due_date &&
        new Date(o.due_date) < new Date()
      ).length;

      return { total, completed, inProgress, pending, overdue };
    },
    staleTime: 30000,
  });
}

export function useQualityStats() {
  return useQuery({
    queryKey: ['quality_stats'],
    queryFn: async () => {
      const { data, error } = await api.from('quality_inspections')
        .select('id, status, defects_count');

      if (error) throw error;

      const inspections = data || [];
      const total = inspections.length;
      const passed = inspections.filter((i: any) => i.status === 'passed').length;
      const failed = inspections.filter((i: any) => i.status === 'failed').length;
      const passRate = total > 0 ? (passed / total) * 100 : 100;

      return { total, passed, failed, passRate };
    },
    staleTime: 60000,
  });
}

export function useLowStockAlerts() {
  return useQuery({
    queryKey: ['low_stock_alerts'],
    queryFn: async () => {
      const { data, error } = await api.from('material_inventory')
        .select('id, name, code, current_stock, min_stock')
        .eq('is_active', true);

      if (error) throw error;

      return (data || []).filter((m: any) =>
        m.current_stock !== null &&
        m.min_stock !== null &&
        m.current_stock <= m.min_stock
      );
    },
    staleTime: 60000,
  });
}

export function useMaintenanceAlerts() {
  return useQuery({
    queryKey: ['maintenance_alerts'],
    queryFn: async () => {
      const today = format(new Date(), 'yyyy-MM-dd');

      const [moldsResult, machinesResult] = await Promise.all([
        api.from('molds')
          .select('id, name, code, current_uses, max_uses, next_maintenance_date')
          .eq('is_active', true),
        api.from('machines')
          .select('id, name, code, last_maintenance')
          .eq('is_active', true),
      ]);

      const moldAlerts = (moldsResult.data || []).filter((m: any) => {
        if (m.max_uses && m.current_uses >= m.max_uses * 0.9) return true;
        if (m.next_maintenance_date && m.next_maintenance_date <= today) return true;
        return false;
      }).map((m: any) => ({ ...m, type: 'mold' }));

      // Machine alerts: flag if last maintenance was >90 days ago
      const ninetyDaysAgo = format(subDays(new Date(), 90), 'yyyy-MM-dd');
      const machineAlerts = (machinesResult.data || []).filter((m: any) =>
        m.last_maintenance && m.last_maintenance <= ninetyDaysAgo
      ).map((m: any) => ({ ...m, type: 'machine' }));

      return [...moldAlerts, ...machineAlerts];
    },
    staleTime: 60000,
  });
}
